import { AfterContentInit, Component, ContentChildren, Input, QueryList } from '@angular/core';
import { PanelComponent } from './panel/panel.component';
import { first, map, startWith, switchMap } from 'rxjs/operators';
import { merge, Observable } from 'rxjs';

@Component({
  selector: 'evsb-accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
})
export class AccordionComponent implements AfterContentInit {
  @Input()
  public closeOthers = false;

  @ContentChildren(PanelComponent)
  protected panels: QueryList<PanelComponent>;

  protected get panels$(): Observable<QueryList<PanelComponent>> {
    return this.panels.changes.pipe(
      map(() => this.panels),
      startWith(this.panels)
    );
  }

  protected get panelStateChanges() {
    return this.panels$.pipe(switchMap(() => this.observablePanels));
  }

  protected get observablePanels(): Observable<PanelComponent> {
    const panelChanges = this.panels.map((panel) => panel.didChange.asObservable());
    return merge(...panelChanges);
  }

  public ngAfterContentInit() {
    this.panelStateChanges.subscribe((changes) => this.handlePanelStateChanges(changes));
  }

  public next(stepIndex: number) {
    const nextPanel = this.panels.toArray()[stepIndex + 1];
    if (nextPanel) {
      if (nextPanel.open) {
        this.scrollToElement(nextPanel);
      } else {
        nextPanel.animationFinished.pipe(first()).subscribe(() => {
          this.scrollToElement(nextPanel);
        });
        nextPanel?.toggle();
      }
    }
  }

  public scrollToElement(nextPanel: PanelComponent) {
    const el = nextPanel?.element.nativeElement as HTMLElement;
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  protected closeAllExcept(current: PanelComponent) {
    this.panels
      .filter((panel) => current !== panel)
      .filter((panel) => panel.open)
      .forEach((openPanel) => (openPanel.open = false));
  }

  protected handlePanelStateChanges(changedPanel: PanelComponent) {
    if (this.closeOthers && changedPanel.open) {
      this.closeAllExcept(changedPanel);
    }
  }
}
